import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, ChevronRight } from "lucide-react";
import { Reveal, Stagger, Item } from "@/components/Reveal";
import SectionLabel from "@/components/SectionLabel";
import ServiceCard from "@/components/ServiceCard";
import { HasMark, CompanyName } from "@/components/BrandText";
import {
  COMPANY, HERO_HIGHLIGHTS, SERVICES, WHY_CHOOSE,
  APPROACH_STEPS, INDUSTRIES, IMAGES,
} from "@/data/site";

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section data-testid="hero" className="relative min-h-[100svh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={IMAGES.hero}
            alt="Dubai skyline at dusk"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-veil" />
          <div className="absolute inset-0 noise" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pb-20 md:pb-28 pt-40 w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="overline mb-5"
          >
            <span>Premium Business Advisory · Dubai</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 0.61, 0.36, 1] }}
            className="font-display text-white text-[44px] sm:text-6xl md:text-7xl lg:text-[88px] leading-[0.98] tracking-tight max-w-5xl"
          >
            Helping Businesses<br />
            <span className="italic font-medium gold-gradient-text">Grow Beyond Borders</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="mt-6 text-lg md:text-2xl text-white/85 font-light tracking-wide"
          >
            Strategic Advisory. Market Intelligence. Business Growth.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.35 }}
            className="mt-6 max-w-2xl text-white/65 leading-relaxed"
          >
            At <CompanyName />, we help businesses, entrepreneurs, developers,
            logistics companies, hospitality brands, and investors unlock opportunities,
            navigate complex markets, and accelerate sustainable growth through research-driven
            consulting and strategic business solutions.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.5 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link to="/contact" data-testid="hero-cta-primary" className="btn-gold">
              Book A Consultation <ArrowRight size={18} />
            </Link>
            <Link to="/services" data-testid="hero-cta-secondary" className="btn-ghost">
              Explore Our Services <ChevronRight size={18} />
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 0.7 }}
            className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-3"
          >
            {HERO_HIGHLIGHTS.map((h) => (
              <div key={h} className="flex items-center gap-2 text-[12px] tracking-wider text-white/70">
                <Check size={14} className="text-[#D4AF37]" />
                <span>{h}</span>
              </div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.4, delay: 0.9 }}
            className="mt-12 pt-6 border-t border-white/10 max-w-xl"
          >
            <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37]">{COMPANY.trust}</p>
          </motion.div>
        </div>

        {/* Bottom gold shimmer */}
        <div className="absolute bottom-0 left-0 right-0 h-px gold-shimmer" />
      </section>

      {/* ABOUT TEASER */}
      <section className="relative py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          <Reveal>
            <div className="overline mb-5">About <HasMark /></div>
            <h2 className="font-display text-3xl md:text-5xl text-white leading-[1.05] tracking-tight">
              Building Sustainable Growth<br />
              Through <span className="italic gold-gradient-text">Strategy & Intelligence</span>
            </h2>
            <p className="mt-6 text-white/70 leading-relaxed text-base md:text-lg">
              <CompanyName /> is a Dubai-based consulting firm dedicated to helping
              organizations improve performance, strengthen market positioning, and unlock new opportunities.
            </p>
            <p className="mt-4 text-white/60 leading-relaxed">
              Our approach combines strategic thinking, market intelligence, industry expertise, and practical
              execution to help businesses achieve measurable results and long-term success.
            </p>
            <div className="mt-8">
              <Link to="/about" data-testid="about-learn-more" className="btn-ghost">
                Learn About Us <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="relative">
              <div className="absolute -top-3 -left-3 w-24 h-24 border-l border-t border-[#D4AF37]/60" />
              <div className="absolute -bottom-3 -right-3 w-24 h-24 border-r border-b border-[#D4AF37]/60" />
              <img
                src={IMAGES.boardroom}
                alt="Executive boardroom"
                className="relative w-full h-[460px] md:h-[560px] object-cover grayscale-[15%]"
              />
              <div className="absolute bottom-4 left-4 right-4 glass-card p-4 flex items-center gap-4">
                <div className="h-12 w-px bg-[#D4AF37]/60" />
                <p className="text-sm text-white/85">
                  <span className="text-[#D4AF37] font-medium">Executive-Grade Advisory</span> — research-driven
                  decisions for boards, founders and investors.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SERVICES */}
      <section className="relative py-24 md:py-32 bg-[#0c0c0c]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10 mb-14">
            <SectionLabel
              overline="Consulting Practices"
              title="Comprehensive Consulting Solutions"
              subtitle="Seven specialised practices, one strategic compass — built to deliver measurable outcomes across markets."
            />
            <Link to="/services" className="btn-ghost self-start lg:self-end" data-testid="services-view-all">
              View All Practices <ArrowRight size={16} />
            </Link>
          </div>

          <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s, i) => (
              <Item key={s.id}><ServiceCard service={s} index={i} /></Item>
            ))}
          </Stagger>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="relative py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionLabel
            align="center"
            overline={<>Why <HasMark /></>}
            title="Strategic Thinking. Practical Solutions. Measurable Growth."
            subtitle="Five reasons leaders across Dubai, India and international markets choose us as their advisory partner."
            className="mb-14 mx-auto"
          />
          <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
            {WHY_CHOOSE.map((w) => {
              const Icon = w.icon;
              return (
                <Item key={w.title}>
                  <div className="lux-card gold-brackets p-6 h-full">
                    <div className="h-12 w-12 flex items-center justify-center border border-[#D4AF37]/30 text-[#D4AF37]">
                      <Icon size={22} strokeWidth={1.5} />
                    </div>
                    <h4 className="font-display text-xl text-white mt-5 leading-snug">{w.title}</h4>
                    <p className="mt-2 text-sm text-white/65 leading-relaxed">{w.desc}</p>
                  </div>
                </Item>
              );
            })}
          </Stagger>
        </div>
      </section>

      {/* INDUSTRIES preview */}
      <section className="relative py-24 md:py-32 bg-[#0c0c0c] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10 mb-12">
            <SectionLabel overline="Industries We Serve" title="Sectors We Know Intimately" />
            <Link to="/industries" className="btn-ghost self-start lg:self-end" data-testid="industries-view-all">
              All Industries <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4">
            {INDUSTRIES.slice(0,6).map((it, i) => (
              <Reveal key={it} delay={i * 0.04}>
                <div className="lux-card p-6 md:p-8 flex items-end justify-between min-h-[140px]">
                  <span className="font-display text-xl md:text-2xl text-white">{it}</span>
                  <span className="text-[10px] tracking-[0.28em] text-[#D4AF37]">0{i+1}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* APPROACH preview */}
      <section className="relative py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionLabel
            overline="Our Approach"
            title="From Discovery to Sustainable Growth"
            subtitle="A disciplined five-stage methodology that transforms business challenges into long-term value."
            className="mb-14"
          />
          <div className="relative">
            <div className="hidden md:block absolute top-7 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/50 to-transparent" />
            <Stagger className="grid grid-cols-1 md:grid-cols-5 gap-6">
              {APPROACH_STEPS.map((st) => (
                <Item key={st.num}>
                  <div className="relative text-center md:text-left">
                    <div className="relative z-10 inline-flex items-center justify-center h-14 w-14 rounded-full border border-[#D4AF37]/40 bg-[#0A0A0A] text-[#D4AF37] font-display text-lg">
                      {st.num}
                    </div>
                    <h4 className="font-display text-xl text-white mt-5">{st.title}</h4>
                    <p className="mt-2 text-sm text-white/60 leading-relaxed">{st.desc}</p>
                  </div>
                </Item>
              ))}
            </Stagger>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.goldAbstract} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/85 to-[#0A0A0A]" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 md:px-10 py-24 md:py-32 text-center">
          <Reveal>
            <div className="overline mb-5 flex justify-center">Get Started</div>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display text-3xl md:text-6xl text-white leading-[1.05] tracking-tight">
              Ready To Accelerate Your <span className="italic gold-gradient-text">Business Growth?</span>
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-6 text-white/70 max-w-2xl mx-auto">
              Partner with <CompanyName /> and gain access to strategic expertise, market intelligence,
              and growth-focused consulting solutions.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-10">
              <Link to="/contact" data-testid="cta-book-consultation" className="btn-gold text-sm tracking-[0.18em] uppercase">
                Book Your Consultation Today <ArrowRight size={18} />
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.28}>
            <p className="mt-10 text-[11px] uppercase tracking-[0.32em] text-[#D4AF37]">{COMPANY.tagline}</p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
